import { Banknote, IndianRupee, Percent, Calendar } from "lucide-react";
import { PieChart as RechartsPieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import formatCurrency from "@/utils/formatCurrency";
import CalculatorInputField from "@/components/shared/CalculatorInputField";
import CalculatorResultCard from "@/components/shared/CalculatorResultCard";
import { useEMICalculator } from "@/hooks/useCalculators";

const EMICalculatorTab = () => {
  const { loanAmount, setLoanAmount, loanRate, setLoanRate, loanYears, setLoanYears, result, chartData, amortizationTable } = useEMICalculator();
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="finance-card">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2"><Banknote className="text-teal-600" size={24} /> Loan EMI Calculator</h2>
          <CalculatorInputField label="Loan Amount" value={loanAmount} onChange={setLoanAmount} icon={IndianRupee} suffix="₹" />
          <CalculatorInputField label="Interest Rate (Annual)" value={loanRate} onChange={setLoanRate} icon={Percent} suffix="%" step={0.1} max={20} />
          <CalculatorInputField label="Loan Tenure" value={loanYears} onChange={setLoanYears} icon={Calendar} suffix="Years" max={30} />
          <div className="grid grid-cols-1 gap-4 mt-6">
            <div className="p-6 rounded-xl bg-teal-100 border-2 border-teal-300"><p className="text-sm text-teal-700 mb-1">Monthly EMI</p><p className="text-3xl font-bold text-teal-600">₹{formatCurrency(result.emi)}</p></div>
            <div className="grid grid-cols-2 gap-4"><CalculatorResultCard label="Principal Amount" value={loanAmount} colorClass="blue" /><CalculatorResultCard label="Total Interest" value={result.totalInterest} colorClass="red" /></div>
            <CalculatorResultCard label="Total Payment" value={result.totalPayment} colorClass="gray" />
          </div>
        </div>
        <div className="finance-card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Loan Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart><Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>{chartData.map((entry) => (<Cell key={entry.name} fill={entry.fill} />))}</Pie><Tooltip formatter={(v) => `₹${formatCurrency(v)}`} /><Legend /></RechartsPieChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-600">Over {loanYears} years, you will pay <span className="font-semibold text-red-600">₹{formatCurrency(result.totalInterest)}</span> as interest on a loan of <span className="font-semibold text-teal-600">₹{formatCurrency(loanAmount)}</span>.</p></div>
        </div>
      </div>

      {/* EMI Amortization Table */}
      <div className="finance-card">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Year-wise EMI Breakdown</h3>
        <div className="table-scroll">
          <table className="w-full min-w-[550px]">
            <thead>
              <tr>
                <th className="py-3 px-3 text-center text-sm font-bold text-white bg-gray-600 rounded-tl-lg">Year</th>
                <th className="py-3 px-3 text-right text-sm font-bold text-white bg-green-600">Principal (A)</th>
                <th className="py-3 px-3 text-right text-sm font-bold text-white bg-orange-500">Interest (B)</th>
                <th className="py-3 px-3 text-right text-sm font-bold text-white bg-gray-500">Total Payment (A+B)</th>
                <th className="py-3 px-3 text-right text-sm font-bold text-white bg-rose-700 rounded-tr-lg">Balance</th>
              </tr>
            </thead>
            <tbody>
              {amortizationTable.map((row) => (
                <tr key={row.year} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-3 text-center font-medium text-gray-700">{row.year} Year</td>
                  <td className="py-3 px-3 text-right text-green-700 font-medium">₹{formatCurrency(row.principal)}</td>
                  <td className="py-3 px-3 text-right text-orange-600 font-medium">₹{formatCurrency(row.interest)}</td>
                  <td className="py-3 px-3 text-right text-gray-700 font-medium">₹{formatCurrency(row.totalPayment)}</td>
                  <td className="py-3 px-3 text-right text-rose-700 font-medium">₹{formatCurrency(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EMICalculatorTab;
